import React, {useContext, useEffect, useState} from "react";
import {useNavigate, useSearchParams} from "react-router-dom";
import useConfirm from "../../../hooks/useConfirm";
import {
  Avatar,
  Box,
  Chip,
  Divider,
  Grid,
  Stack,
  Tab,
  Tabs,
  Typography
} from "@mui/material";
import {doc, writeBatch} from "firebase/firestore";
import {db} from "../../../config/firebase";
import {PROJECT_VERSION_COLLECTION, PROJECTS_COLLECTION} from "../../../db/collections";
import {toast} from "react-toastify";
import {IconCamera} from "@tabler/icons-react";

import {Context as AppContext} from "../../../context/AppContext";
import {ProjectUpdateForm} from "./ProjectUpdateForm";
import {ProjectMembersSettings} from "./ProjectMembersSettings";
import axios from "../../../config/axios";
import {OrganizationRepository} from "../../../repositories/OrganizationRepository";
import {SessionUMRepository} from "../../../repositories/SessionUMRepository";
import {SignInContext, TOKEN} from "../../../context/SignInContext";
import {v4 as uuid} from "uuid";
import {uploadImage} from "../../../utils/uploadImage";
import {FlowProjectRepository} from "../../../repositories/FlowProjectRepository";
import {FlowProtectionSettings} from "./FlowProtectionSettings";

const TABS = ["general", "members"];

export const ProjectSettings = ({project}) => {
  const {updateProject, publishVersion} = useContext(AppContext);
  const {currentUser} = useContext(SignInContext);
  const [newProject, setNewProject] = useState({});
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [users, setUsers] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [asriProjects, setAsriProjects] = useState([]);
  const [image, setImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const confirmDelete = useConfirm(
    'Are you sure you want to delete this project?',
  );

  const activeTab = TABS.includes(searchParams.get("tab")) ? searchParams.get("tab") : "general";
  const handleTabChange = (e, value) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (value === "general") next.delete("tab"); else next.set("tab", value);
      return next;
    }, {replace: true});
  };

  useEffect(() => {
    if (project) {
      setNewProject({
        id: project?.id,
        name: project?.name,
        description: project?.description,
        organization: project?.settings?.organization,
        language: project?.settings?.language,
        lang: project?.settings?.lang || null,
        organization_type: project?.settings?.organization_type,
        speechToText: project?.settings?.speechToText,
        voiceGemini: project?.settings?.voiceGemini || null,
        host: project?.settings?.host,
        port: project?.settings?.port,
        protocol: project?.settings?.protocol,
        mode: project?.settings?.mode || "stage",
        deploymentProject: project?.settings?.deploymentProject || null,
        team: project?.team || [],
        is_live: !!project?.configuration?.is_live,
        status: !!project?.settings?.status ? project?.settings?.status : "BUILDING_SCENARIOS",
        projectType: !!project?.settings?.projectType ? project?.settings?.projectType : "VOICE_BOT",
      });
      setImage(project?.settings?.img || null);
    }
  }, [project]);

  useEffect(() => {
    if (currentUser) {
      fetchData();
    }
  }, [currentUser]);

  const fetchData = async () => {
    try {
      const response = await axios.get('/api/language-support/all');
      setLanguages(response.data);
      const organizationResponse = await OrganizationRepository.getUserOrganizations(currentUser?.username);
      setOrganizations(organizationResponse?.data || []);
      SessionUMRepository.getUserSessions()
        .then(response => {
          setAsriProjects(response?.data?.filter(item => item?.module?.moduleType === "SPEECH_TO_TEXT") || []);
        })
        .catch(err => console.log(err));
      const userResponse = await axios.get('/api/user/all', null, {
        headers: {
          "Authorization": `Bearer ${localStorage.getItem(TOKEN)}`
        }
      });
      const users = userResponse.data?.map(u => u.username);
      setUsers(users);
    } catch (err) {
      toast.error(err.message);
    }
  }


  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const org = organizations?.find(org => org?.id === newProject?.organization);
      const imgURL = await createImage();

      let formData = {...project}
      formData.name = newProject?.name;
      formData.description = newProject?.description;
      formData.team = newProject?.team || [];
      formData.settings.organization = newProject?.organization;
      formData.settings.language = newProject?.language;
      formData.settings.lang = newProject?.lang;
      formData.settings.organization_type = org?.organization_type || "";
      formData.settings.speechToText = newProject?.speechToText || null;
      formData.settings.voiceGemini = newProject?.voiceGemini || null;
      formData.settings.host = newProject?.host || null;
      formData.settings.port = newProject?.port || null;
      formData.settings.protocol = newProject?.protocol || null;
      formData.settings.mode = newProject?.mode || null;
      formData.settings.deploymentProject = newProject?.deploymentProject || null;
      formData.settings.status = newProject?.status || "BUILDING_SCENARIOS";
      formData.settings.projectType = newProject?.projectType || "VOICE_BOT";
      formData.settings.img = imgURL;
      if (!formData?.configuration){
        formData.configuration = {};
      }
      formData.configuration.is_live = newProject?.is_live === undefined || newProject?.is_live === null ? true : newProject?.is_live;
      console.log(formData);

      await publishVersion(formData);
      const fetchVersion = `${process.env.REACT_APP_FIREBASE_FUNCTIONS_URL}/api/apiProject/${project.id}`
      const projectVersionResponse = await axios.get(fetchVersion)
      await FlowProjectRepository.updateFlow({id: project?.id, project: projectVersionResponse?.data || null})
      toast.success("Project settings updated");
    } catch (e) {
      toast.error(e?.message || "Failed to create project")
    } finally {
      setLoading(false);
    }
  }

  const createImage = async () => {
    if (!image || image === project?.settings?.img) {
      return  image;
    }
    const id = uuid();
    const url =`projects/${id}-${image?.name}`;

    return await uploadImage({img: image, url: url});
  }

  const onDelete = async () => {
    const response = await confirmDelete();
    if (response && newProject?.id) {
      try {
        const batch = writeBatch(db);
        // Delete versions
        if (newProject?.versions?.length) {
          for (const version of newProject?.versions) {
            // Delete version
            const versionRef = doc(db, PROJECT_VERSION_COLLECTION, version?.id);
            batch.delete(versionRef);
          }
        }

        // Delete project
        const projectRef = doc(db, PROJECTS_COLLECTION, newProject?.id || newProject?.uid);
        batch.delete(projectRef);

        await batch.commit();
        // await getUserProjects();

        navigate("/projects")
        // onClose();
      } catch (err) {
        toast.error(err);
      }

      return;
    }
  }


  const imageSrc = image !== null ? (typeof image === "string" ? image : URL.createObjectURL(image)) : null;

  return (
    <>
      <Grid container sx={{ px: {xs: 2, md: 0}, py: {xs: 2, md: 3}, width: "100%", overflowY: "auto"}}>
        <Grid item xs={12} md={8} sx={{mx: "auto", p: {xs: 0, md: 2}, pb: {xs: 2, md: 3}}}>
          <Typography variant={"h4"} fontSize={{xs: "1.5rem", md: "2rem"}} fontWeight={700}>
            Project Settings
          </Typography>
          <Typography variant={"body2"} color={"text.secondary"} sx={{mt: 0.5}}>
            Manage {project?.name ? <strong>{project.name}</strong> : "this project"}'s profile, deployment and access.
          </Typography>
        </Grid>

        <Grid item xs={12} md={8} sx={{mx: "auto", p: {xs: 0, md: 2}}}>
          <Stack direction={"column"} gap={0} sx={{borderRadius: {xs: 2, md: 4}, overflow: {xs: "hidden", md: "visible"}}} className={"border-shadow"}>

            {/* Persistent profile header - stays put while the tabs below switch */}
            <Stack direction={"row"} alignItems={"center"} textAlign={"left"} gap={1.5} sx={{pt: {xs: 3, md: 4}, px: {xs: 2, md: 4}, pb: {xs: 2, md: 3}}}>
              <Box component={"label"} sx={{position: "relative", display: "inline-flex", cursor: "pointer"}}>
                <Avatar
                  src={imageSrc}
                  sx={{
                    bgcolor: "rgba(132, 145, 247, 0.1)",
                    height: {xs: 80, md: 96},
                    width: {xs: 80, md: 96},
                    fontSize: {xs: 28, md: 34},
                    borderRadius: 4,
                    color: "primary.main"
                  }}
                >
                  {image ? null : <IconCamera size={30}/>}
                </Avatar>
                <Box sx={{
                  position: "absolute",
                  bottom: -4,
                  right: -4,
                  width: 30,
                  height: 30,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "primary.main",
                  color: "primary.contrastText",
                  border: "3px solid #fff",
                  boxShadow: "0 2px 8px rgba(132, 145, 247, 0.5)"
                }}>
                  <IconCamera size={14}/>
                </Box>
                <input
                  type="file"
                  hidden
                  onChange={(event) => setImage(event.target.files[0])}
                  accept="image/*"
                />
              </Box>
              <Box>
                <Typography fontWeight={600} fontSize={"1.05rem"}>
                  {newProject?.name || "Untitled Project"}
                </Typography>
                <Typography variant={"body2"} color={"text.secondary"}>
                  Click the avatar to change the image
                </Typography>
                <Stack direction={"row"} gap={1} flexWrap={"wrap"} justifyContent={"start"} sx={{pt: 1.5}}>
                  <Chip
                    size={"small"}
                    label={newProject?.is_live ? "Live" : "Maintenance"}
                    color={newProject?.is_live ? "success" : "warning"}
                    sx={{fontWeight: 500}}
                  />
                  <Chip
                    size={"small"}
                    variant={"outlined"}
                    label={newProject?.projectType === "CHAT_BOT" ? "Chatbot Assistant" : "Voice Assistant"}
                  />
                </Stack>
              </Box>
            </Stack>
            <Divider sx={{mx: {xs: 2, md: 4}}}/>

            {/* Tabs - same card, same chrome, only the content below switches */}
            <Box sx={{px: {xs: 2, md: 4}}}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                sx={{
                  minHeight: 44,
                  "& .MuiTabs-indicator": {height: 3, borderRadius: 3},
                  "& .MuiTab-root": {textTransform: "none", minHeight: 44, minWidth: "auto", fontWeight: 500, px: 0, mr: 3}
                }}
              >
                <Tab label={"General"} value={"general"}/>
                <Tab label={"Members"} value={"members"}/>
              </Tabs>
            </Box>
            <Divider/>

            {activeTab === "general" &&
              <Box component={"form"} onSubmit={handleSubmit}>
                <ProjectUpdateForm
                  project={newProject} setProject={setNewProject}
                  languages={languages} organizations={organizations}
                  fetchData={fetchData} loading={loading}
                  asriProjects={asriProjects} users={users}
                  onDelete={onDelete}
                />
              </Box>
            }
            {activeTab === "members" &&
              <ProjectMembersSettings project={project}/>
            }
          </Stack>
        </Grid>

        <Grid item xs={12} md={8} sx={{mx: "auto", p: {xs: 0, md: 2}, pt: {xs: 1, md: 0}}}>
          <FlowProtectionSettings project={project}/>
        </Grid>

      </Grid>

    </>
  )
}
